import { z } from 'zod';

// Beneficiary "source" — where the record came from.
export const BeneficiarySourceSchema = z.enum(['erp', 'user']);
export type BeneficiarySource = z.infer<typeof BeneficiarySourceSchema>;

// Provider-specific key/value bag (account_number, account_currency, account_name,
// account_type, nickname...). Backend-driven and open-ended → loose string map.
export const BeneficiaryDetailsSchema = z.record(z.string(), z.string().optional());

// GET beneficiaries/{id} — confirmed against a live response. NOTE: the entity has
// NO vendorDisplayName (that lives on the grid ROW, not the entity). details keys +
// overall shape vary by payment provider → passthrough(); the dev [API SCHEMA]
// validator surfaces drift to tighten.
export const BeneficiarySchema = z
  .object({
    id: z.string(),
    vendorId: z.string(),
    providerName: z.string(),
    source: BeneficiarySourceSchema,
    isDefault: z.boolean(),
    details: BeneficiaryDetailsSchema,
  })
  .passthrough();
export type Beneficiary = z.infer<typeof BeneficiarySchema>;

// GET beneficiaries/form — confirmed: { formTitle, formSubTitle, fields[] }.
// Field set/types vary by provider; passthrough() for provider extras. The
// `type` enum mirrors the modal's local BeneficiaryField type + its renderer —
// if a provider ever sends a new type, the dev [API SCHEMA] validator surfaces
// it (extend this enum AND the modal's ./types together).
export const BeneficiaryFieldSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    type: z.enum(['text', 'email', 'select', 'multi-select']),
    placeholder: z.string().optional(),
    helpText: z.string().optional(),
    subscription: z.string().optional(),
    options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
    defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
  })
  .passthrough();
export const BeneficiaryFormSchema = z
  .object({
    formTitle: z.string(),
    formSubTitle: z.string(),
    fields: z.array(BeneficiaryFieldSchema),
  })
  .passthrough();

// Path / query.
export const BeneficiaryIdPathSchema = z.object({ id: z.string().min(1) });
export const BeneficiaryDetailsQuerySchema = z.object({ isExternal: z.boolean() }).partial();
export const VendorIdPathSchema = z.object({ vendorId: z.string().min(1) });

// Request bodies — NOTE the deliberate casing asymmetry (create=snake, update=camel).
export const BeneficiaryCreateBodySchema = z.object({
  details: BeneficiaryDetailsSchema,
  vendor_id: z.string(),
  is_default: z.boolean(),
  source: BeneficiarySourceSchema,
});
export const BeneficiaryUpdateBodySchema = z
  .object({
    details: BeneficiaryDetailsSchema,
    isDefault: z.boolean(),
    source: BeneficiarySourceSchema,
  })
  .partial();
// Vendor field: body keyed by grid field name (withholdingTaxRate | withholdingTaxExpiryDate).
export const VendorFieldBodySchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]));
export const VendorFieldResponseSchema = z.object({ updatedPaymentsCount: z.number() }).passthrough();

// POST beneficiaries returns ONLY the new id — NOT the full entity (that's the
// GET beneficiaries/{id} shape, BeneficiarySchema). Payment-API writes return
// minimal bodies; we only consume `.id`. passthrough() tolerates extra fields.
export const BeneficiaryCreateResponseSchema = z.object({ id: z.string() }).passthrough();

// Column `type` is an enum ON PURPOSE: a backend grid column whose type we don't
// render must surface (dev [API SCHEMA] throw) — the "control unknown header types"
// guard. When you add a type, extend this enum AND the grid's column-type→renderer
// mapping together. (Observed: string/amount/percent/date.)
export const GridColumnTypeSchema = z.enum(['string', 'amount', 'percent', 'date']);
export const PaymentGridColumnSchema = z
  .object({
    field: z.string(),
    headerName: z.string(),
    type: GridColumnTypeSchema,
    sortable: z.boolean(),
    filter: z.boolean(),
    hide: z.boolean(),
    operator: z.string().nullable(),
    editable: z.boolean().optional(),
  })
  .passthrough();
export const BeneficiariesGridHeadersSchema = z.object({
  vendor: z.array(PaymentGridColumnSchema),
  beneficiary: z.array(PaymentGridColumnSchema),
});

// GET grid/beneficiaries/total returns a BARE number (e.g. 7), not an object.
export const BeneficiariesGridTotalSchema = z.number();

// Backend-driven grid; rows vary by provider → loose rows, typed envelope.
export const BeneficiariesGridSearchBodySchema = z
  .object({
    filterModel: z.unknown().optional(),
    sortModel: z.unknown().optional(),
    paginationModel: z.object({ limit: z.number(), offset: z.number() }),
    searchTerm: z.string().optional(),
  })
  .passthrough();
export const BeneficiariesGridSearchResponseSchema = z.object({
  items: z.array(z.record(z.string(), z.unknown())),
  count: z.number(),
});
